/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, useState, useEffect } from "react";
import { Box, Typography, Stack } from "@mui/material";
import {
  CustomDivider,
  getStartTimeOfTask,
  getPointDescription,
  PanelContentsArea,
} from "./Util";
import { useMapDataContext } from "context/MapDataProvider";
import { useTranslation } from "react-i18next";
import { PlanJobInfoVehicleBase } from "client/models/PlanJobInfoVehicleBase";
import { styled } from "@mui/material/styles";
import { DisplayItem, DisplayItemValue } from "components/Common/DisplayItem";
import { Const } from "const";
import { ResultTaskList } from "client";
import { useAwvDataContext } from "context/AwvDataProvider";
import { DisplayItemWithTimeIcon } from "./DisplayItemWithTimeIcon";
import moment from "moment";
import { BorderedButton } from "components/Common/BorderedButton";

/**
 * Check edited.
 * edited is inputDate < updateDate.
 * @param {Date} inputDate
 * @param {Date} updateDate
 * @return {boolean} isEdited
 */
function isEdited(inputDate?: Date, updateDate?: Date | null): boolean {
  const inputMomentDate = inputDate ? moment(inputDate) : moment.invalid();
  const updateMomentDate = updateDate ? moment(updateDate) : moment.invalid();
  // judge edited
  let edited = false;
  if (inputMomentDate.isValid() && updateMomentDate.isValid()) {
    edited = inputMomentDate.isBefore(updateMomentDate);
  }
  return edited;
}

interface TaskDetailPanelProps {
  task: ResultTaskList;
  onClose?: () => void;
}

export const TaskDetailPanelNoButton: FC<TaskDetailPanelProps> = ({
  task,
  onClose,
}) => {
  const { t } = useTranslation();
  const { mapData } = useMapDataContext();
  const { awvData } = useAwvDataContext();
  const [nameId, setNameId] = useState<string | null>();

  useEffect(() => {
    setNameId(
      awvData?.awvInfos?.find(
        (awv) => awv.frameTypeAndNumber === task.frameTypeAndNumber
      )?.nameId
    );
  }, [awvData, task.frameTypeAndNumber]);

  const displayValue =
    task.taskKind === 4
      ? {
          startNodeName: "-",
          planTaskExecutionTime: "-",
          startTimeOfTask: "-",
          memo: "-",
        }
      : {
          startNodeName: getPointDescription(
            task.startNodeId!,
            mapData,
            t("AWVUI-MSG119")
          ),
          planTaskExecutionTime:
            task.planTaskExecutionTime === undefined
              ? "-"
              : task.planTaskExecutionTime + " " + t("AWVUI-MSG54"),
          startTimeOfTask: getStartTimeOfTask(
            task.startDatetime,
            t("AWVUI-MSG59")
          ),
          memo: task.memo ?? "",
        };
  return (
    <Container>
      <HeaderArea>
        <Stack direction="row">
          <HeaderContentsArea>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle1">{t("AWVUI-MSG36")}:</Typography>
              {isEdited(task.inputDate, task.updateDate) && (
                <Typography variant="subtitle1" paddingRight={"0.2rem"}>
                  {t("AWVUI-MSG79")}
                </Typography>
              )}
            </Stack>
            <DisplayItemValue variant="h6">{task.jobSetName!}</DisplayItemValue>
          </HeaderContentsArea>
        </Stack>
        <DisplayItem
          title={t("AWVUI-MSG123")}
          value={task.taskIdDisp ? task.taskIdDisp : ""}
        />
      </HeaderArea>
      <PanelContentsArea>
        <CustomBox>
          <DisplayItem
            title={t("AWVUI-MSG35")}
            value={`${t("AWVUI-MSG77")}-${nameId ?? "--"}`}
          />
          <CustomDivider />
          <DisplayItemWithTimeIcon
            isAutoStart={!!task.extensionTimeLoad}
            title={t("AWVUI-MSG37")}
            value={displayValue.startNodeName}
          />
          {task.jobInfos?.map((item: PlanJobInfoVehicleBase, index: number) => (
            <Box key={`wrap-${index}`}>
              <DisplayItemWithTimeIcon
                key={index}
                isAutoStart={!!item.extensionTimeUnload}
                title={t("AWVUI-MSG109")}
                value={getPointDescription(
                  item.unloadingNodeId,
                  mapData,
                  t("AWVUI-MSG119")
                )}
              />
            </Box>
          ))}
          <DisplayItemWithTimeIcon
            isAutoStart={false}
            title={t("AWVUI-MSG38")}
            value={getPointDescription(
              task.endNodeId!,
              mapData,
              t("AWVUI-MSG119")
            )}
          />
          <CustomDivider />
          <DisplayItem
            title={t("AWVUI-MSG39")}
            value={displayValue.planTaskExecutionTime}
          />
        </CustomBox>
        <CustomBox>
          <DisplayItem
            title={t("AWVUI-MSG108")}
            value={displayValue.startTimeOfTask}
          />
          <CustomDivider />
          <DisplayItem title={t("AWVUI-MSG41")} value={displayValue.memo} />
        </CustomBox>
        <CustomBox>
          <DisplayItem
            title={t("AWVUI-MSG64")}
            value={task.inputUserId ?? ""}
          />
          <DisplayItem
            title={t("AWVUI-MSG94")}
            value={
              (isEdited(task.inputDate, task.updateDate)
                ? task.updateUserId
                : undefined) ?? "--"
            }
          />
        </CustomBox>
      </PanelContentsArea>
      <BorderedButton onClick={onClose}>
        {t("AWVUI-MSG22")}
      </BorderedButton>
    </Container>
  );
};

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
}));

const CustomBox = styled(Box)(({ theme }) => ({
  width: "100%",
  borderBottom: "1px solid black",
  padding: "0.5rem 2rem",
}));

const HeaderArea = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(1, 4),
  width: "100%",
  flexDirection: "column",
  backgroundColor: Const.sidePanelHeaderColor,
}));

const HeaderContentsArea = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
}));

